import { Slot } from '@radix-ui/react-slot';
import { cn } from '@veranda/shared/lib';
import { stegaClean } from 'next-sanity';
import type { CSSProperties, ReactNode } from 'react';

type HeadingConfigProps = {
  fontFamily: string;
  fontSize: string;
  backgroundColor: string | null;
  backgroundPosition: string | null;
} | null;

interface HeadingProps {
  children: ReactNode;
  config?: HeadingConfigProps;
  className?: string;
}

export function StyledHeading({ config, children, className }: HeadingProps) {
  if (!config || !config?.fontFamily || !config?.fontSize) {
    return (
      <Slot className={cn('typography-caslon-h1 text-primary', className)}>
        {children}
      </Slot>
    );
  }

  const typographyClasses = {
    outfit: {
      h1: 'typography-outfit-h1',
      h2: 'typography-outfit-h2',
      h3: 'typography-outfit-h3',
      h4: 'typography-outfit-h4',
      h5: 'typography-outfit-h5',
      h6: 'typography-outfit-h6',
      body: 'typography-outfit-body',
    },
    caslon: {
      h1: 'typography-caslon-h1',
      h2: 'typography-caslon-h2',
      h3: 'typography-caslon-h3',
      h4: 'typography-caslon-h4',
      h5: 'typography-caslon-h5',
      h6: 'typography-caslon-h6',
      body: 'typography-caslon-body',
    },
    clash: {
      h1: 'typography-clash-h1',
      h2: 'typography-clash-h2',
      h3: 'typography-clash-h3',
      h4: 'typography-clash-h4',
      h5: 'typography-clash-h5',
      h6: 'typography-clash-h6',
    },
    abramo: {
      h1: 'typography-abramo-h1',
      h2: 'typography-abramo-h2',
      h3: 'typography-abramo-h3',
      h4: 'typography-abramo-h4',
      h5: 'typography-abramo-h5',
    },
  } as const;

  const typographyClass =
    typographyClasses[config.fontFamily as keyof typeof typographyClasses]?.[
      config.fontSize as keyof (typeof typographyClasses)[keyof typeof typographyClasses]
    ] || 'typography-caslon-h1';

  const baseClasses = cn(typographyClass, 'text-primary', className);

  // Handle background with offset effect
  if (config.backgroundColor && config.backgroundPosition === 'bottom') {
    return (
      <Slot
        className={cn(
          baseClasses,
          'relative after:absolute after:top-1/2 after:left-[-4%] after:z-[-1] after:h-[80%] after:w-[108%] after:bg-[var(--bg-color)]'
        )}
        style={
          {
            '--bg-color': config.backgroundColor,
          } as CSSProperties & { '--bg-color': string }
        }
      >
        {children}
      </Slot>
    );
  }

  // Handle regular background (aligned)
  if (config.backgroundColor) {
    return (
      <Slot
        className={baseClasses}
        style={{
          backgroundColor: config.backgroundColor
            ? stegaClean(config.backgroundColor)
            : undefined,
        }}
      >
        {children}
      </Slot>
    );
  }

  return <Slot className={baseClasses}>{children}</Slot>;
}
